import React, { HTMLAttributes } from 'react'

import styled from 'styled-components'
import {
  color,
  ColorProps,
  layout,
  LayoutProps,
  margin,
  MarginProps,
  typography,
  TypographyProps,
} from 'styled-system'
import { themeGet } from '@styled-system/theme-get'

import { TEXT_SHOULD_FORWARD_PROP } from 'Config/shouldForwardProp'

import { centerCss, CustomAlignProps, leftCss, rightCss } from './align'
import {
  accentCss,
  CustomColorProps,
  disabledCss,
  primaryCss,
  secondaryCss,
} from './color'
import {
  bodyCss,
  CustomColor002Props,
  importantCss,
  inverseCss,
  successCss,
} from './color002'
import {
  capitalizeCss,
  CustomExtendedProps,
  ellipsisCss,
  monospaceCss,
  nowrapCss,
  preWrapCss,
  uppercaseCss,
} from './extended'
import {
  body1Css,
  body2Css,
  captionCss,
  CustomTypographyProps,
  header5Css,
  header6Css,
  overlineCss,
  subtitle2Css,
} from './typography'
import {
  CustomTypography002Props,
  screen14Css,
  screen16Css,
  screen18Css,
  screen20Css,
  screen24Css,
  screen28Css,
  screen32Css,
  screen36Css,
  screen38Css,
  screen40Css,
  screen52Css,
  screen64Css,
} from './typography002'
import { boldCss, CustomWeightProps, mediumCss, regularCss } from './weight'

type AsProps = {
  as?: 'span' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div'
}

type CustomProps = CustomExtendedProps &
  CustomColorProps &
  CustomAlignProps &
  CustomTypographyProps &
  CustomWeightProps &
  CustomTypography002Props &
  CustomColor002Props

type StyledTextProps = AsProps &
  CustomProps &
  ColorProps &
  LayoutProps &
  MarginProps &
  TypographyProps

const shouldForwardProp = (prop: string) =>
  !TEXT_SHOULD_FORWARD_PROP.includes(prop)

const StyledText = styled.p.withConfig({ shouldForwardProp })`
  margin: 0;
  padding: 0;
  position: relative;

  font-family: ${themeGet('fonts.primary')};
  font-size: 14px;
  font-weight: normal;
  line-height: 1.2em;

  ${layout}
  ${margin}

  ${accentCss}
  ${disabledCss}
  ${primaryCss}
  ${secondaryCss}
  ${importantCss}
  ${inverseCss}
  ${successCss}
  ${bodyCss}

  ${body1Css}
  ${body2Css}
  ${captionCss}
  ${header5Css}
  ${header6Css}
  ${overlineCss}
  ${subtitle2Css}

  ${screen14Css}
  ${screen16Css}
  ${screen18Css}
  ${screen20Css}
  ${screen24Css}
  ${screen28Css}
  ${screen32Css}
  ${screen36Css}
  ${screen38Css}
  ${screen40Css}
  ${screen52Css}
  ${screen64Css}

  ${capitalizeCss}
  ${ellipsisCss}
  ${monospaceCss}
  ${nowrapCss}
  ${uppercaseCss}
  ${preWrapCss}

  ${leftCss}
  ${centerCss}
  ${rightCss}

  ${regularCss}
  ${mediumCss}
  ${boldCss}

  ${color}
  ${typography}
`

type Props = HTMLAttributes<HTMLParagraphElement> &
  StyledTextProps & {
    children: React.ReactNode
  }

function Text({ children, ...rest }: Props) {
  return <StyledText {...rest}>{children}</StyledText>
}

export default Text
